import { useRef, useState } from 'react';
import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer';
import { TextNode, EditorState, LexicalEditor } from 'lexical';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableRowNode } from '@lexical/table';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ClickableLinkPlugin as LexicalClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { $rootTextContent } from '@lexical/text';
import { $generateHtmlFromNodes } from '@lexical/html';
import {
  ContentEditableStyled,
  RichTextEditorContainer,
  RichTextEditorInnerContainer,
  RichTextEditorPlaceholder,
} from './RichTextEditor.styles';
import SetContentsPlugin from './plugins/SetContentsPlugin';
import EditablePlugin from './plugins/EditablePlugin';
import ControlsPlugin from './plugins/ControlsPlugin';
import { RichTextEditorProps } from './RichTextEditor.types';
import ClearEditorPlugin from './plugins/ClearEditorPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import OnBlurPlugin from './plugins/OnBlurPlugin';
import { DivNode } from './plugins/nodes/DivNode';
import { ExtendedTextNode } from './plugins/nodes/ExtendedTextNode';
import { ImageNode } from './plugins/nodes/ImageNode';
import { TableStyledNode } from './plugins/nodes/TableStyledNode';
import { TableCellStyledNode } from './plugins/nodes/TableCellStyledNode';
import { LinkStyledNode } from './plugins/nodes/LinkStyledNode';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import { RichTextEditorDefaultTheme } from './RichTextEditor.themes';
import EditLinkPlugin from './plugins/EditLinkPlugin';
import HTMLEditModePlugin from './plugins/HTMLEditModePlugin';
import PreviewPlugin from './plugins/preview/PreviewPlugin';
import InsertParagraphPlugin from './plugins/InsertParagraphPlugin';

const editorConfig: InitialConfigType = {
  namespace: 'hostfullyRichTextEditor',
  nodes: [
    AutoLinkNode,
    DivNode,
    ImageNode,
    HeadingNode,
    LinkStyledNode,
    {
      replace: LinkNode,
      with: (node: LinkNode) => LinkStyledNode.importJSON(node.exportJSON()),
      withKlass: LinkStyledNode,
    },
    ListNode,
    ListItemNode,
    QuoteNode,
    ExtendedTextNode,
    {
      replace: TextNode,
      with: (node: TextNode) => new ExtendedTextNode(node.__text),
      withKlass: ExtendedTextNode,
    },
    TableCellStyledNode,
    TableRowNode,
    TableStyledNode,
  ],
  onError: (error) => {
    throw error;
  },
  theme: RichTextEditorDefaultTheme,
};

const RichTextEditor = ({
  id,
  contents,
  placeholderText,
  controlsSettings,
  customFooter,
  linkMode = 'open',
  onChange: onChangeProp,
  onBlur,
  readOnly = false,
  clearContentsOnReadOnly = false,
  toolbarSettings,
  stripHTML = false,
  useInsertParagraphPlugin = true,
}: RichTextEditorProps) => {
  const hasRunOnChangeOnce = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const htmlEditorRef = useRef<HTMLTextAreaElement>(null);
  const [isHTMLEditModeEnabled, setHTMLEditModeEnabled] = useState(false);
  const [isPreviewModeEnabled, setIsPreviewModeEnabled] = useState(false);

  const onChange = (_: EditorState, editor: LexicalEditor) => {
    // Skips first pass, as `onChange` gets called when the editor gets initialized making forms think there are changes
    // within the editor when in reality nothing was changed yet.
    if (!hasRunOnChangeOnce.current) {
      hasRunOnChangeOnce.current = true;
      return;
    }

    editor.update(() => {
      const htmlContent = stripHTML
        ? $rootTextContent()
        : $generateHtmlFromNodes(editor);

      onChangeProp?.(htmlContent);
    });
  };

  const placeholder = placeholderText && (
    <RichTextEditorPlaceholder>{placeholderText}</RichTextEditorPlaceholder>
  );

  const isRichTextEditorEnabled =
    !isPreviewModeEnabled && !isHTMLEditModeEnabled;

  return (
    <LexicalComposer
      initialConfig={{
        ...editorConfig,
        editable: !readOnly,
      }}
    >
      <RichTextEditorContainer
        $isDisabled={isPreviewModeEnabled}
        id={id}
        ref={containerRef}
      >
        {toolbarSettings?.enabled && (
          <ToolbarPlugin
            htmlEditorRef={htmlEditorRef}
            isHTMLEditModeEnabled={isHTMLEditModeEnabled}
            isPreviewModeEnabled={isPreviewModeEnabled}
            setHTMLEditModeEnabled={setHTMLEditModeEnabled}
            setIsPreviewModeEnabled={setIsPreviewModeEnabled}
            toolbarSettings={toolbarSettings}
          />
        )}
        <RichTextEditorInnerContainer>
          {isRichTextEditorEnabled && (
            <RichTextPlugin
              contentEditable={
                <ContentEditableStyled id={`${id}-editable-area`} />
              }
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              placeholder={placeholder}
              ErrorBoundary={LexicalErrorBoundary}
            />
          )}
          {isHTMLEditModeEnabled && (
            <HTMLEditModePlugin id={id} ref={htmlEditorRef} />
          )}
          {isPreviewModeEnabled && (
            <PreviewPlugin toolbarSettings={toolbarSettings} />
          )}
          <AutoLinkPlugin />
          {controlsSettings.clearEditorButton && (
            <ClearEditorPlugin setSuggestion={controlsSettings.setSuggestion} />
          )}
          <ControlsPlugin controlsSettings={controlsSettings} />
          <HistoryPlugin />
          <ListPlugin />
          <OnChangePlugin onChange={onChange} />
          <OnBlurPlugin onBlur={onBlur} />
          <SetContentsPlugin contents={contents} />
          <EditablePlugin
            editable={!readOnly}
            clearContentsOnDisable={clearContentsOnReadOnly}
          />
          {linkMode === 'open' && <LexicalClickableLinkPlugin newTab />}
          {linkMode === 'edit' && (
            <>
              <LinkPlugin />
              <EditLinkPlugin container={containerRef.current} />
            </>
          )}
          {useInsertParagraphPlugin && <InsertParagraphPlugin />}
        </RichTextEditorInnerContainer>
      </RichTextEditorContainer>
      {customFooter!}
    </LexicalComposer>
  );
};

RichTextEditor.defaultProps = {
  initialContent: undefined,
  placeholderText: '',
};

export default RichTextEditor;
